import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';

interface GuideLayoutProps {
  title: string;
  description: string;
  breadcrumbLabel: string;
  breadcrumbLink?: string;
  readTime: string;
  imageUrl: string;
  imageAlt: string;
  children: React.ReactNode;
  internalLinks: { label: string; to?: string; href?: string; icon?: string }[];
}

function LinkButton({ link }: { link: GuideLayoutProps['internalLinks'][number] }) {
  const classes = "inline-flex items-center gap-1.5 px-5 py-2.5 bg-background-50 text-foreground-700 text-sm rounded-full border border-background-300/60 hover:border-primary-300 hover:text-primary-500 transition-all whitespace-nowrap cursor-pointer";
  const iconEl = link.icon ? (
    <span className="w-4 h-4 flex items-center justify-center">
      <i className={`${link.icon} text-xs`}></i>
    </span>
  ) : null;

  if (link.href) {
    return (
      <a href={link.href} target="_blank" rel="noopener nofollow" className={classes}>
        {iconEl}
        {link.label}
      </a>
    );
  }

  return (
    <Link to={link.to || '/'} className={classes}>
      {iconEl}
      {link.label}
    </Link>
  );
}

export default function GuideLayout({
  title,
  description,
  breadcrumbLabel,
  breadcrumbLink = '/guide',
  readTime,
  imageUrl,
  imageAlt,
  children,
  internalLinks,
}: GuideLayoutProps) {
  return (
    <>
      <Navbar />
      <main className="pt-16">
        {/* Article Header with Image */}
        <section className="relative w-full">
          <div className="relative h-[280px] md:h-[380px] overflow-hidden">
            <img
              src={imageUrl}
              alt={imageAlt}
              title={title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/35"></div>
          </div>
          <div className="absolute inset-0 flex items-end">
            <div className="w-full px-6 md:px-10 pb-10 md:pb-14">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-2 text-sm text-background-50/80 mb-3">
                  <Link to="/" className="hover:text-background-50 transition-colors cursor-pointer">
                    Home
                  </Link>
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-xs"></i>
                  </span>
                  <Link to={breadcrumbLink} className="hover:text-background-50 transition-colors cursor-pointer">
                    Guides
                  </Link>
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-xs"></i>
                  </span>
                  <span className="text-background-50/60">{breadcrumbLabel}</span>
                </div>
                <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-3">
                  {title}
                </h1>
                <p className="text-sm md:text-base text-background-50/80 max-w-2xl">
                  {description}
                </p>
                <div className="flex flex-wrap items-center gap-4 text-xs text-background-50/70 mt-4">
                  <span className="flex items-center gap-1.5">
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-user-line"></i>
                    </span>
                    Alex Carter
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-calendar-line"></i>
                    </span>
                    Updated: July 13, 2026
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-time-line"></i>
                    </span>
                    {readTime}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Article Body */}
        <article className="w-full px-6 md:px-10 py-10 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="prose-custom space-y-8 text-foreground-700 leading-relaxed">
              {children}
            </div>

            <div className="border-t border-background-200/70 pt-6 mt-10">
              <p className="text-xs text-foreground-400">
                <strong className="text-foreground-500">Editorial Note:</strong> This guide was
                last reviewed on July 13, 2026. Content is maintained according to our editorial
                standards for accuracy and relevance.
              </p>
            </div>
          </div>
        </article>

        {/* Internal Links */}
        <section className="w-full px-6 md:px-10 py-10 md:py-14 bg-background-100/70">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-2">
              Explore More Resources
            </h3>
            <p className="text-sm text-foreground-600 mb-6">
              Continue your shopping discovery journey with these helpful guides.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-background-50 text-foreground-700 text-sm rounded-full border border-background-300/60 hover:border-primary-300 hover:text-primary-500 transition-all whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-home-line text-xs"></i>
                </span>
                Superbuy Sheet Home
              </Link>
              {internalLinks.map((link, idx) => (
                <LinkButton key={idx} link={link} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}